import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Divider,
  Grid,
  Chip,
  useTheme,
  alpha,
  Paper,
} from "@mui/material";
import { format } from "date-fns";
import CloseIcon from "@mui/icons-material/Close";
import DataObjectIcon from "@mui/icons-material/DataObject";

const LogDialog = ({ open, log, onClose }) => {
  const theme = useTheme();
  const [showMetadata, setShowMetadata] = useState(false);

  if (!log) return null;

  const handleMetadataToggle = () => setShowMetadata(!showMetadata);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM dd, yyyy hh:mm:ss a");
    } catch {
      return "-";
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("deliver") || s.includes("sent")) return "success";
    if (s.includes("fail") || s.includes("bounce") || s.includes("drop"))
      return "error";
    if (s.includes("defer") || s.includes("queue")) return "warning";
    return "default";
  };

  const DetailItem = ({ label, value, isStatus, compact = false }) => (
    <Box sx={{ mb: compact ? 1 : 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight="600"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.3 }}>
        {isStatus ? (
          <Chip
            label={value}
            color={getStatusColor(value)}
            size="small"
            variant="filled"
            sx={{
              fontWeight: 600,
              borderRadius: "4px",
              height: "24px",
              fontSize: "0.75rem",
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-word",
              fontWeight: 500,
              fontSize: "0.9rem",
            }}
          >
            {value || "-"}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 2,
          backdropFilter: "blur(20px)",
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          pt: 2,
          px: 2.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight="700"
            sx={{ fontSize: "1rem" }}
          >
            Log Details
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            EUID: {log.euid}
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          color="inherit"
          size="small"
          sx={{ minWidth: "auto", p: 0.5, borderRadius: "50%" }}
        >
          <CloseIcon sx={{ fontSize: "20px" }} />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, px: 2.5, pb: 1.5 }}>
        {/* Primary Info Grid */}
        <Grid container mt={1} spacing={1.5}>
          <Grid item xs={12}>
            <DetailItem label="Subject" value={log.subject} compact />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailItem label="Status" value={log.status} isStatus compact />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailItem label="Log Type" value={log.type} compact />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailItem label="From Email" value={log.from_email_id} compact />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailItem
              label="Recipients"
              value={(log.to_email_ids || []).join(", ")}
              compact
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5, borderStyle: "dashed" }} />

        {/* Timestamps Grid */}
        <Typography
          variant="caption"
          color="primary"
          sx={{
            mb: 1,
            fontWeight: 700,
            fontSize: "0.7rem",
            display: "block",
            textTransform: "uppercase",
          }}
        >
          Timestamps
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <DetailItem
              label="Log Time"
              value={formatDate(log.log_timestamp)}
              compact
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <DetailItem
              label="Email Time"
              value={formatDate(log.email_timestamp)}
              compact
            />
          </Grid>
        </Grid>

        {log.status_description && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: alpha(theme.palette.error.main, 0.05),
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
            }}
          >
            <Typography
              variant="caption"
              color="error"
              fontWeight="bold"
              sx={{
                fontSize: "0.7rem",
                display: "block",
                textTransform: "uppercase",
              }}
            >
              Status Description
            </Typography>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ mt: 0.5, display: "block", fontSize: "0.85rem" }}
            >
              {log.status_description}
            </Typography>
          </Box>
        )}

        {/* Metadata Section */}
        <Box sx={{ mt: 2 }}>
          <Button
            onClick={handleMetadataToggle}
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<DataObjectIcon sx={{ fontSize: "16px" }} />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontSize: "0.8rem",
              py: 0.7,
            }}
          >
            {showMetadata ? "Hide Metadata" : "View Metadata"}
          </Button>

          {showMetadata && (
            <Paper
              elevation={0}
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: alpha(theme.palette.text.primary, 0.03),
                borderRadius: 1.5,
                overflowX: "auto",
                border: `1px solid ${theme.palette.divider}`,
                maxHeight: "300px",
                overflow: "auto",
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: "0.7rem",
                  fontFamily: "monospace",
                  color: theme.palette.text.secondary,
                  lineHeight: "1.3",
                }}
              >
                {JSON.stringify(log.meta_data || {}, null, 2)}
              </pre>
            </Paper>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LogDialog;
